import React, { useRef, useState, useEffect, useCallback } from 'react';
import Slider from 'react-slick';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";

const PlotMarketListView = ({
  id,
  name,
  address,
  price,
  image,
  images = [],
  amenities = [],
  nearby = [],
  perSqft,
  area,
  description,
  categoryname,
  postedOn,
  lastUpdate,
  verified,
  soldStatus,
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Internal refs to hold slider instances (no <any> → fixes ESLint no-undef)
  const previewSliderInstance = useRef(null);
  const cardSliderInstance = useRef(null);

  const navigate = useNavigate();

  const cardSliderSettings = {
    dots: false,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: false,
    adaptiveHeight: false,
  };

  // Build full images list
  const allImages = Array.isArray(images) && images.length > 0
    ? images
    : image ? [image] : ["/assets/images/placeholder_img.png"];

  const showNavArrows = allImages.length > 1;

  const isVideo = (url) => {
    if (typeof url !== 'string') return false;
    return /\.(mp4|webm|mkv|mov|avi)$/i.test(url);
  };

  const handleOpenPreview = (index = 0) => {
    setActiveIndex(index);
    setImgOpen(true);
    // Small delay to ensure slider is mounted before calling slickGoTo
    setTimeout(() => {
      previewSliderInstance.current?.slickGoTo?.(index, true); // true = no animation
    }, 150);
  };

  // Callback ref for dialog preview slider
  const previewSliderRefCallback = useCallback((slider) => {
    previewSliderInstance.current = slider;
  }, []);

  // Callback ref for card mini-slider
  const cardSliderRefCallback = useCallback((slider) => {
    cardSliderInstance.current = slider;
  }, []);

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleCardClick = () => {
    navigate(`/plot-marketplace/details/${id}`);
  };

  const videoRefs = useRef({});
  const [playingVideo, setPlayingVideo] = useState(null);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [loadedMedia, setLoadedMedia] = useState({});

  const handleMediaLoad = (src) => {
    setLoadedMedia((prev) => ({ ...prev, [src]: true }));
  };

  const togglePlay = (e, src, isPreview = false) => {
    e.stopPropagation();
    const refKey = isPreview ? `preview_${src}` : `card_${src}`;
    const video = videoRefs.current[refKey];
    if (video) {
      if (video.paused) {
        if (playingVideo && playingVideo !== src) {
          const otherRefKey = isPreview ? `preview_${playingVideo}` : `card_${playingVideo}`;
          const otherVideo = videoRefs.current[otherRefKey];
          if (otherVideo) otherVideo.pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  return (
    <>
      {/* IMAGE SLIDER PREVIEW DIALOG */}
      <Dialog
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden", bgcolor: "rgba(0,0,0,0.85)" },
        }}
      >
        <IconButton
          onClick={() => setImgOpen(false)}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 20 },
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            position: "relative", 
            height: { xs: 300, sm: 480, md: 600 }, 
            overflow: "hidden",
            "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div": {
              height: "100% !important",
            },
          }}>
            {/* LEFT ARROW */}
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderInstance.current?.slickPrev?.()}
                sx={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  color: "#fff",
                  bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            <Slider
              ref={previewSliderRefCallback}
              initialSlide={activeIndex}
              dots={false}
              infinite={false}
              speed={400}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              beforeChange={(old, next) => {
                Object.values(videoRefs.current).forEach((vid) => {
                  if (vid) vid.pause();
                });
                setPlayingVideo(null);
              }}
            >
              {allImages.map((img, i) => (
                <Box key={i}>
                  <Box
                    sx={{
                      height: { xs: 300, sm: 480, md: 600 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,0,0,0.7)",
                      p: 1,
                    }}
                  >
                    {isVideo(img) ? (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        {!loadedMedia[`preview_${img}`] && (
                           <Skeleton variant="rectangular" width="100%" height="100%" sx={{ position: "absolute", top: 0, left: 0 }} />
                        )}
                        <video
                          ref={(el) => (videoRefs.current[`preview_${img}`] = el)}
                          src={img}
                          playsInline
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() => handleMediaLoad(`preview_${img}`)}
                          onClick={(e) => togglePlay(e, img, true)}
                          style={{ width: "100%", height: "100%", objectFit: "contain", display: loadedMedia[`preview_${img}`] ? "block" : "none", cursor: "pointer" }}
                        />
                        {loadedMedia[`preview_${img}`] && (
                          <Box
                            onClick={(e) => togglePlay(e, img, true)}
                            sx={{
                              position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                              bgcolor: "rgba(0,0,0,0.5)", color: "white", width: 60, height: 50, display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", borderRadius: "4px", zIndex: 2,
                            }}
                          >
                            {playingVideo === img ? <PauseIcon sx={{ fontSize: 40 }} /> : <PlayArrowIcon sx={{ fontSize: 40 }} />}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        {!loadedMedia[`preview_${img}`] && (
                           <Skeleton variant="rectangular" width="100%" height="100%" sx={{ position: "absolute", top: 0, left: 0 }} />
                        )}
                        <img
                          src={img}
                          alt={`${name || "Plot"} ${i + 1}`}
                          onLoad={() => handleMediaLoad(`preview_${img}`)}
                          style={{ width: "100%", height: "100%", objectFit: "contain", display: loadedMedia[`preview_${img}`] ? "block" : "none" }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>

            {/* RIGHT ARROW */}
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderInstance.current?.slickNext?.()}
                sx={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 3,
                  color: "#fff",
                  bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}

            {/* Slide counter */}
            {showNavArrows && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  borderRadius: "12px",
                  px: 1.5,
                  py: 0.3,
                  fontSize: 12,
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              >
                {activeIndex + 1} / {allImages.length}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* MAIN CARD */}
      <Card
        onClick={handleCardClick}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: 4,
          p: { xs: 1, sm: 1.5 },
          overflow: "hidden",
          width: "100%",
          mx: "auto",
          gap: { xs: 1, sm: 2 },
          boxShadow: "0 4px 12px 6px rgba(0,0,0,0.08)",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px) scale(1.01)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.14)",
          },
        }}
      >
        {/* IMAGE SECTION */}
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", sm: "42%" },
            height: { xs: "auto", sm: 255 },
            flexShrink: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {soldStatus === 1 && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                bgcolor: "#d32f2f",
                color: "#fff",
                px: 2,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: 14,
                fontWeight: 800,
                zIndex: 3,
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                letterSpacing: "0.5px",
              }}
            >
              Sold Out
            </Box>
          )}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              opacity: soldStatus === 1 ? 0.6 : 1,
              filter: soldStatus === 1 ? "grayscale(20%)" : "none",
              "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div": {
                height: "100% !important",
              },
            }}
          >
          {allImages.length > 1 ? (
            <Box sx={{ position: "relative", height: "100%" }}>
              <Slider 
                ref={cardSliderRefCallback} 
                {...cardSliderSettings}
                beforeChange={() => {
                  if (playingVideo) {
                    const video = document.querySelector(`video[src="${playingVideo}"]`);
                    if (video) video.pause();
                    setPlayingVideo(null);
                  }
                }}
              >
                {allImages.map((img, i) => (
                  <Box key={i}>
                    {isVideo(img) ? (
                      <Box
                        onMouseEnter={() => setHoveredVideo(img)}
                        onMouseLeave={() => setHoveredVideo(null)}
                        sx={{ position: "relative", width: "100%", height: 255 }}
                      >
                        {!loadedMedia[`card_${img}`] && (
                           <Skeleton variant="rectangular" width="100%" height="100%" sx={{ position: "absolute", top: 0, left: 0, borderRadius: 3 }} />
                        )}
                        <CardMedia
                          component="video"
                          ref={(el) => (videoRefs.current[`card_${img}`] = el)}
                          src={img}
                          playsInline
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() => handleMediaLoad(`card_${img}`)}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePlay(e, img, false);
                          }}
                          sx={{
                            width: "100%",
                            height: 255,
                            borderRadius: 3,
                            objectFit: "cover",
                            cursor: "pointer",
                            display: loadedMedia[`card_${img}`] ? "block" : "none",
                          }}
                        />
                        {loadedMedia[`card_${img}`] && (playingVideo !== img || hoveredVideo === img) && (
                          <IconButton
                            onClick={(e) => togglePlay(e, img, false)}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              borderRadius: "4px",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                            }}
                          >
                            {playingVideo === img ? <PauseIcon /> : <PlayArrowIcon />}
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ position: "relative", width: "100%", height: 255 }}>
                        {!loadedMedia[`card_${img}`] && (
                           <Skeleton variant="rectangular" width="100%" height="100%" sx={{ position: "absolute", top: 0, left: 0, borderRadius: 3 }} />
                        )}
                        <CardMedia
                          component="img"
                          image={img}
                          alt={`${name || "Plot"} ${i + 1}`}
                          onLoad={() => handleMediaLoad(`card_${img}`)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenPreview(i);
                          }}
                          sx={{
                            width: "100%",
                            height: 255,
                            borderRadius: 3,
                            objectFit: "cover",
                            cursor: "zoom-in",
                            display: loadedMedia[`card_${img}`] ? "block" : "none",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Slider>

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  cardSliderInstance.current?.slickPrev?.();
                }}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  bgcolor: "rgba(255,255,255,0.7)",
                  p: 0.5,
                  "&:hover": { bgcolor: "white" },
                  display: { xs: "none", sm: "flex" },
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  cardSliderInstance.current?.slickNext?.();
                }}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  bgcolor: "rgba(255,255,255,0.7)",
                  p: 0.5,
                  "&:hover": { bgcolor: "white" },
                  display: { xs: "none", sm: "flex" },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>

              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 12,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  borderRadius: "6px",
                  px: 1,
                  py: 0.3,
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                +{allImages.length - 1} more
              </Box>
            </Box>
          ) : (
            isVideo(allImages[0]) ? (
              <Box
                onMouseEnter={() => setHoveredVideo(allImages[0])}
                onMouseLeave={() => setHoveredVideo(null)}
                sx={{ position: "relative", width: "100%", height: { xs: 200, sm: 255 } }}
              >
                <CardMedia
                  component="video"
                  src={allImages[0]}
                  playsInline
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload nopictureinpicture"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlay(e, allImages[0]);
                  }}
                  sx={{
                    width: "100%",
                    height: { xs: 200, sm: 255 },
                    borderRadius: 3,
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                />
                {(playingVideo !== allImages[0] || hoveredVideo === allImages[0]) && (
                  <IconButton
                    onClick={(e) => togglePlay(e, allImages[0])}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      borderRadius: "4px",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    {playingVideo === allImages[0] ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                )}
              </Box>
            ) : (
              <CardMedia
                component="img"
                image={allImages[0] || "/assets/images/placeholder_img.png"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPreview(0);
                }}
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 255 },
                  borderRadius: 3,
                  objectFit: "cover",
                  cursor: "zoom-in",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.04)" },
                }}
              />
            )
          )}
          </Box>
        </Box>

        {/* RIGHT CONTENT */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: { xs: 1, sm: 1.5 },
            opacity: soldStatus === 1 ? 0.65 : 1,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Typography
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: { xs: 2, sm: 1, lg: 2 },
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: { xs: 500, sm: 600, lg: 700 },
                fontSize: { xs: "0.95rem", sm: "1.05rem", lg: "1.2rem" },
              }}
            >
              {name || ""}
            </Typography>

            {/* LOCATION */}
            <Box display="flex" alignItems="center" mt={0.5} mb={0.8}>
              <LocationOn
                sx={{
                  fontSize: { xs: "0.9rem", sm: "1.1rem", lg: "1.4rem" },
                  color: "#9DC354",
                  mr: 0.5,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13, md: 14 },
                  color: "#707070",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {address || ""}
              </Typography>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "#ccc", my: 0.8, borderWidth: 1.5 }} />

            {/* PRICE & AREA */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 0.8,
                gap: 1.5,
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <PriceBlock
                icon={<CurrencyRupeeIcon sx={{ color: "#9DC354", fontSize: 18 }} />}
                value={price}
                label="Price"
              />
              <VDivider />
              <PriceBlock
                icon={<CurrencyRupeeIcon sx={{ color: "#9DC354", fontSize: 18 }} />}
                value={area}
                label="Total Area"
              />
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "#ccc", my: 0.8, borderWidth: 1.5 }} />

            {/* VERIFIED STATUS */}
            <Box
              display="flex"
              alignItems="center"
              mt={1}
              sx={{ border: "1px solid #ccc", borderRadius: 10, px: 1, py: 0.5 }}
            >
              <img
                src={verified === 1 ? "/assets/images/verified.png" : "/assets/icons/not_verified.png"}
                width={14}
                alt="verified status"
              />
              <Typography fontSize={{ xs: 11, sm: 12 }} sx={{ ml: 0.5 }}>
                {verified === 1 ? "Verified Plot" : "Not Verified"}
              </Typography>
            </Box>

            {/* DATES */}
            <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
              <Box>
                <Typography fontSize={{ xs: 11, sm: 12 }} color="#608900">
                  {formatDate(postedOn)}
                </Typography>
                <Typography fontSize={{ xs: 11, sm: 12 }}>Posted On</Typography>
              </Box>
              <Box>
                <Typography fontSize={{ xs: 11, sm: 12 }} color="#608900">
                  {formatDate(lastUpdate)}
                </Typography>
                <Typography fontSize={{ xs: 11, sm: 12 }}>Last Update</Typography>
              </Box>
            </Box>
          </CardContent>

          {/* VIEW DETAILS BUTTON */}
          <Box display="flex" justifyContent="center" mt={1.5}>
            <Button
              variant="contained"
              sx={{
                width: 140,
                height: 36,
                textTransform: "none",
                bgcolor: "#FDBA15",
                color: "black",
                "&:hover": { bgcolor: "#e3a30f" },
                borderRadius: 20,
                fontWeight: 500,
                fontSize: 14,
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plot-marketplace/details/${id}`);
              }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default PlotMarketListView;

/* Helper Components */

const PriceBlock = ({ icon, value, label }) => (
  <Box>
    <Box display="flex" alignItems="center" gap={0.4}>
      {icon}
      <Typography sx={{ fontSize: { xs: 14, sm: 14 }, fontWeight: 600 }}>
        {value || "-"}
      </Typography>
    </Box>
    <Typography color="text.secondary" sx={{ fontSize: { xs: 14, sm: 14 } }}>
      {label}
    </Typography>
  </Box>
);

const VDivider = () => (
  <Divider
    orientation="vertical"
    flexItem
    sx={{ borderStyle: "dashed", mx: 1.2, borderWidth: 1.3 }}
  />
);